<template>
  <div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Dokument importieren</h5>
          <button type="button" class="btn-close" @click="$emit('close')" aria-label="Close"></button>
        </div>

        <div class="modal-body">
          <p class="mb-3 text-muted">
            Importiere eine bearbeitete Rechnung als <strong>ODT</strong>, <strong>DOCX</strong> oder <strong>PDF</strong>.
            ODT/DOCX werden zu PDF konvertiert und anschließend als ZUGFeRD mit den strukturierten Rechnungsdaten erzeugt.
          </p>

          <div class="mb-3">
            <label class="form-label" for="invoiceDocumentUpload">Datei (ODT/DOCX/PDF)</label>
            <input
              id="invoiceDocumentUpload"
              type="file"
              class="form-control"
              accept=".pdf,.odt,.ott,.docx"
              @change="uploadDocument"
              data-testid="invoice-import-upload"
            />
            <small v-if="documentFileId" class="text-success d-block mt-2">
              Datei-ID: {{ documentFileId }} ({{ uploadedFileName || "hochgeladen" }})
            </small>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label" for="importZugferdVersion">ZUGFeRD Version</label>
              <select id="importZugferdVersion" class="form-select" v-model="selectedZUGFeRDVersionId" data-testid="invoice-import-zugferd-version">
                <option v-for="version in masterDataStore.ZUGFeRDVersions" :key="version.id" :value="version.id">
                  {{ version.name }} ({{ version.id }})
                </option>
              </select>
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label" for="importZugferdProfile">ZUGFeRD Profil</label>
              <select id="importZugferdProfile" class="form-select" v-model="selectedZUGFeRDProfileId" data-testid="invoice-import-zugferd-profile">
                <option v-for="profile in masterDataStore.ZUGFeRDProfiles" :key="profile.id" :value="profile.id">
                  {{ profile.name }} ({{ profile.id }})
                </option>
              </select>
            </div>
          </div>

          <div v-if="isOpenOfficeDocument" class="mt-2">
            <div class="form-check mb-2">
              <input id="importRunAiReview" class="form-check-input" type="checkbox" v-model="runAiReview" data-testid="invoice-import-run-ai" />
              <label class="form-check-label" for="importRunAiReview">
                ChatGPT-Prüfung vor der PDF/ZUGFeRD-Erzeugung (nur ODT/OTT)
              </label>
            </div>

            <div class="mb-3">
              <label class="form-label" for="importChatGptModel">ChatGPT Modell</label>
              <select
                id="importChatGptModel"
                class="form-select"
                v-model="chatGptModel"
                :disabled="!runAiReview"
                data-testid="invoice-import-chatgpt-model"
              >
                <option value="">Backend Default</option>
                <option value="gpt-5.1-mini">gpt-5.1-mini (schneller/günstiger)</option>
                <option value="gpt-5.1">gpt-5.1 (bessere Qualität)</option>
              </select>
              <small class="text-muted d-block mt-1">
                Leer = Backend-Default. Wird nur verwendet, wenn die KI-Prüfung aktiv ist.
              </small>
            </div>

            <div class="form-check">
              <input
                id="importAutoFixOpenOfficeDocument"
                class="form-check-input"
                type="checkbox"
                v-model="autoFixOpenOfficeDocument"
                data-testid="invoice-import-autofix"
              />
              <label class="form-check-label" for="importAutoFixOpenOfficeDocument">
                Bekannte Platzhalter-/Formatprobleme automatisch korrigieren (Best-Effort)
              </label>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Abbrechen</button>
          <button
            type="button"
            class="btn btn-success"
            :disabled="!documentFileId"
            data-testid="invoice-import-convert-button"
            @click="submitImport"
          >
            Importieren & umwandeln
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useMasterDataStore } from "@/stores/masterDataStore";
import InvoiceDocumentService from "@/services/InvoiceDocumentService";

export default {
  name: "InvoiceImportModal",
  emits: ["close", "import"],
  data() {
    return {
      documentFileId: "",
      uploadedFileName: "",
      selectedZUGFeRDVersionId: "230",
      selectedZUGFeRDProfileId: "32",
      runAiReview: true,
      autoFixOpenOfficeDocument: true,
      chatGptModel: "",
      masterDataStore: useMasterDataStore(),
    };
  },
  computed: {
    isOpenOfficeDocument() {
      const name = (this.uploadedFileName || "").toLowerCase();
      return name.endsWith(".odt") || name.endsWith(".ott");
    },
  },
  methods: {
    async uploadDocument(event) {
      const file = event?.target?.files?.[0];
      if (!file) return;

      try {
        const uploadResponse = await InvoiceDocumentService.uploadInvoiceDocument(file);
        this.documentFileId = uploadResponse?.data?.id || "";
        this.uploadedFileName = file.name;
      } catch (error) {
        console.error("Failed to upload invoice document:", error);
      }
    },
    submitImport() {
      this.$emit("import", {
        documentFileId: this.documentFileId,
        zugferdVersionId: this.selectedZUGFeRDVersionId,
        zugferdProfileId: this.selectedZUGFeRDProfileId,
        runAiReview: this.isOpenOfficeDocument ? this.runAiReview : false,
        autoFixOpenOfficeDocument: this.isOpenOfficeDocument ? this.autoFixOpenOfficeDocument : false,
        chatGptModel: this.isOpenOfficeDocument ? (this.chatGptModel || null) : null,
      });
    },
  },
  mounted() {
    if (!this.masterDataStore.ZUGFeRDProfiles?.length || !this.masterDataStore.ZUGFeRDVersions?.length) {
      this.masterDataStore.fetchZUGFeRDProfiles();
      this.masterDataStore.fetchZUGFeRDVersions();
    }
  },
};
</script>

<style scoped>
.modal.fade.show {
  display: block;
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
